<script setup>
import DuplicatePostAction from '@frontJs/components/actions/projectManagement/smo/DuplicatePostAction.vue';
import ReschedulePostsAction from '@frontJs/components/actions/projectManagement/smo/ReschedulePostsAction.vue';
import ArrowClockwiseIcon from '@iconify-vue/ph/arrow-clockwise';
import ArrowCounterClockwiseIcon from '@iconify-vue/ph/arrow-counter-clockwise';
import CalendarBlankIcon from '@iconify-vue/ph/calendar-blank';
import CopySimpleIcon from '@iconify-vue/ph/copy-simple';
import ListPlusIcon from '@iconify-vue/ph/list-plus';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import PaperPlaneTiltIcon from '@iconify-vue/ph/paper-plane-tilt';
import PlusIcon from '@iconify-vue/ph/plus';
import ProhibitIcon from '@iconify-vue/ph/prohibit';
import TrashIcon from '@iconify-vue/ph/trash';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import ImagesCell from '@js/components/tables/cells/ImagesCell.vue';
import LinkButtonCell from '@js/components/tables/cells/LinkButtonCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import {
    clearToasts,
    toastError,
    toastSuccess,
    toastUpdated,
} from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h } from 'vue';

const { tenant, status, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    status: {
        type: Array,
        required: false,
        default: () => [],
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const statusTypes = {
    draft: 'secondary',
    scheduled: 'primary',
    publishing: 'primary',
    published: 'success',
    failed: 'danger',
    canceled: 'secondary',
};

function refresh() {
    updateData();

    if (callback) {
        callback();
    }
}

function cancelPosts(ids) {
    axios
        .post(
            route('front.projectManagement.smo.postCancelPosts', {
                tenant: tenant.id,
            }),
            { ids },
        )
        .then(() => {
            clearToasts();
            toastUpdated();
            refresh();
        })
        .catch(toastError);
}

/**
 * Publishing never happens in the request: the call queues the post and dispatches the job,
 * so the status moves from "scheduled" to "published" with a delay.
 */
function publishPost(id) {
    axios
        .post(
            route('front.projectManagement.smo.postPublishPost', {
                tenant: tenant.id,
                smoPost: id,
            }),
        )
        .then(() => {
            clearToasts();
            toastSuccess('La publication a été mise en file de publication.');
            refresh();
        })
        .catch(toastError);
}

function reactivatePosts(ids) {
    axios
        .post(
            route('front.projectManagement.smo.postReactivatePosts', {
                tenant: tenant.id,
            }),
            { ids },
        )
        .then(() => {
            clearToasts();
            toastUpdated();
            refresh();
        })
        .catch(toastError);
}

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    updateData,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'medias',
            header: () => 'Visuels',
            cell: (props) =>
                props.row.original.medias.length
                    ? h(ImagesCell, {
                          images: props.row.original.medias.map(
                              (media) => media.url,
                          ),
                          size: 'small',
                      })
                    : h(TextCell, { text: '-' }),
        }),
        columnHelper.accessor(() => null, {
            id: 'content',
            header: () => 'Contenu',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.content,
                    length: 80,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'scheduledAt',
            header: () => 'Publication prévue',
            cell: (props) =>
                h(TextCell, {
                    text: formatDate(
                        props.row.original.scheduledAt,
                        false,
                        true,
                    ),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'status',
            header: () => 'Statut',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.status.label,
                    type: statusTypes[props.row.original.status.value],
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'remoteScheduledAt',
            header: () => 'Chez Facebook',
            cell: (props) =>
                props.row.original.isRemotelyScheduled
                    ? h(PillCell, {
                          pillValue: 'Programmée',
                          type: 'success',
                      })
                    : h(TextCell, { text: 'En attente' }),
        }),
        columnHelper.accessor(() => null, {
            id: 'externalPermalink',
            header: () => 'Publication',
            cell: (props) =>
                props.row.original.externalPermalink
                    ? h(LinkButtonCell, {
                          href: props.row.original.externalPermalink,
                          target: '_blank',
                      })
                    : h(TextCell, { text: '-' }),
        }),
        columnHelper.accessor(() => null, {
            id: 'errorMessage',
            header: () => 'Erreur',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.errorMessage ?? '-',
                    length: 80,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                icon: NotePencilIcon,
                                title: 'Modifier',
                                type: 'primary',
                                href: route(
                                    'front.projectManagement.smo.post',
                                    {
                                        tenant: tenant.id,
                                        smoPost: props.row.original.id,
                                    },
                                ),
                            }),
                            h(
                                DuplicatePostAction,
                                {
                                    tenant,
                                    smoPost: props.row.original,
                                    callback: refresh,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            icon: CopySimpleIcon,
                                            title: 'Dupliquer',
                                            type: 'primary',
                                        }),
                                },
                            ),
                            ['draft', 'scheduled'].includes(
                                props.row.original.status.value,
                            )
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      icon: PaperPlaneTiltIcon,
                                      title: 'Publier maintenant',
                                      onClick: () =>
                                          publishPost(props.row.original.id),
                                  })
                                : null,
                            props.row.original.status.value === 'failed'
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      icon: ArrowClockwiseIcon,
                                      title: 'Réessayer',
                                      onClick: () =>
                                          publishPost(props.row.original.id),
                                  })
                                : null,
                            props.row.original.status.value === 'canceled'
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      icon: ArrowCounterClockwiseIcon,
                                      title: 'Réactiver',
                                      onClick: () =>
                                          reactivatePosts([
                                              props.row.original.id,
                                          ]),
                                  })
                                : null,
                            props.row.original.isEditable &&
                            props.row.original.status.value !== 'canceled'
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      icon: ProhibitIcon,
                                      title: 'Annuler',
                                      onClick: () =>
                                          cancelPosts([props.row.original.id]),
                                  })
                                : null,
                            h(
                                DeleteAction,
                                {
                                    url: route(
                                        'front.projectManagement.smo.deletePost',
                                        {
                                            tenant: tenant.id,
                                            smoPost: props.row.original.id,
                                        },
                                    ),
                                    callback: refresh,
                                    title: 'Supprimer la publication',
                                    content:
                                        'Êtes-vous sûr de vouloir supprimer cette publication ?',
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            icon: TrashIcon,
                                            type: 'danger',
                                            title: 'Supprimer',
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.smo.postPosts', {
        tenant: tenant.id,
    }),
    exportUrl: route('front.projectManagement.smo.postExportPosts', {
        tenant: tenant.id,
    }),
    data: {
        status: status,
    },
    selectionIdKey: 'id',
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        errorMessage: status.includes('failed'),
        externalPermalink: status.includes('published'),
        remoteScheduledAt: status.includes('scheduled'),
    },
});

/**
 * "Select all" adds an 'all' marker to the array: bulk actions operate on the rows actually
 * selected, so that fake id has to be filtered out.
 */
const selectedIds = computed(() =>
    selectionIds.value.filter((id) => id !== 'all'),
);
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
            />

            <ReschedulePostsAction
                :tenant="tenant"
                :ids="selectedIds"
                :callback="refresh"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :disabled="selectedIds.length === 0"
                        type="primary"
                    >
                        Replanifier la sélection
                    </ActionButton>
                </template>
            </ReschedulePostsAction>

            <ActionButton
                v-if="status.includes('canceled')"
                size="small"
                :left-icon="ArrowCounterClockwiseIcon"
                :disabled="selectedIds.length === 0"
                @click="reactivatePosts(selectedIds)"
            >
                Réactiver la sélection
            </ActionButton>

            <ActionButton
                v-else
                size="small"
                :left-icon="ProhibitIcon"
                :disabled="selectedIds.length === 0"
                @click="cancelPosts(selectedIds)"
            >
                Annuler la sélection
            </ActionButton>

            <DeleteAction
                :url="
                    route('front.projectManagement.smo.deletePosts', {
                        tenant: tenant.id,
                    })
                "
                :config="{ data: { ids: selectedIds } }"
                :callback="refresh"
                title="Supprimer les publications"
                :content="`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} publication(s) ? Cette action est irréversible.`"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        type="danger"
                        :left-icon="TrashIcon"
                        :disabled="selectedIds.length === 0"
                    >
                        Supprimer la sélection
                    </ActionButton>
                </template>
            </DeleteAction>

            <ActionButton
                size="small"
                type="secondary"
                :href="
                    route('front.projectManagement.smo.calendar', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="CalendarBlankIcon"
            >
                Calendrier
            </ActionButton>

            <ActionButton
                size="small"
                type="secondary"
                :href="
                    route('front.projectManagement.smo.batchPosts', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="ListPlusIcon"
            >
                Saisie multiple
            </ActionButton>

            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.smo.post', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
